import Quill from "quill";
import "quill/dist/quill.snow.css";
import React, { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import ValidatorUtils from "../../../utils/ValidatorUtils";
import { normalizeQuillLists } from "../../../utils/ObjectUtils";

const toolbar = [
  ["bold", "italic"],
  [{ list: "ordered" }, { list: "bullet" }],
  ["clean"]
];

const formats = ["bold", "italic", "list"];

const RichTextEditor = ({ htmlContent, disabled, onTextChange, short, isIncomplete, maxLength }) => {
  const editorRef = useRef(null);
  const quillRef = useRef(null);
  const onTextChangeRef = useRef(onTextChange);
  const maxLengthRef = useRef(maxLength);
  const acceptedLengthRef = useRef(ValidatorUtils.getTextLength(htmlContent));
  const intl = useIntl();
  const [lengthLimitReached, setLengthLimitReached] = useState(false);

  useEffect(() => {
    onTextChangeRef.current = onTextChange;
  }, [onTextChange]);

  useEffect(() => {
    maxLengthRef.current = maxLength;
  }, [maxLength]);

  useEffect(() => {
    if (!editorRef.current) return undefined;
    const editorElement = editorRef.current;

    const quill = new Quill(editorElement, {
      formats,
      modules: { toolbar },
      placeholder: "Text",
      readOnly: disabled,
      theme: "snow"
    });
    quillRef.current = quill;

    if (htmlContent) {
      quill.setContents(quill.clipboard.convert({ html: htmlContent, text: "" }), "silent");
    }
    acceptedLengthRef.current = ValidatorUtils.getTextLength(quill.root.innerHTML);

    let textChangeTimeout;
    const handleTextChange = (delta, oldContents, source) => {
      if (source !== "user") return;

      const quillHtml = quill.root.innerHTML;
      const nextValue = normalizeQuillLists(quillHtml);
      const nextLength = ValidatorUtils.getTextLength(nextValue);
      const previousLength = acceptedLengthRef.current;
      const configuredMaxLength = maxLengthRef.current;
      const exceedsLimit = Number.isFinite(configuredMaxLength) && nextLength > configuredMaxLength;

      if (exceedsLimit && nextLength > previousLength) {
        const changeIndex = delta.ops?.[0]?.retain || 0;
        quill.setContents(oldContents, "silent");
        setLengthLimitReached(true);
        window.setTimeout(() => {
          quill.focus();
          quill.setSelection(Math.min(changeIndex, quill.getLength() - 1), 0, "silent");
        }, 0);
        return;
      }

      setLengthLimitReached(false);
      acceptedLengthRef.current = nextLength;
      // Replacing a selection emits deletion and insertion separately. Only publish
      // the final value so React cannot restore the intermediate deleted content.
      window.clearTimeout(textChangeTimeout);
      textChangeTimeout = window.setTimeout(() => onTextChangeRef.current(nextValue, false), 0);
    };

    quill.on("text-change", handleTextChange);

    return () => {
      window.clearTimeout(textChangeTimeout);
      quill.off("text-change", handleTextChange);
      const toolbarElement = quill.getModule("toolbar")?.container;
      toolbarElement?.remove();
      editorElement.innerHTML = "";
      editorElement.className = "";
      quillRef.current = null;
    };
    // Quill must be created exactly once. Prop changes are synchronized below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const quill = quillRef.current;
    if (!quill) return;

    quill.enable(!disabled);
    const toolbarElement = quill.getModule("toolbar")?.container;
    if (toolbarElement) toolbarElement.style.display = disabled ? "none" : "";
  }, [disabled]);

  useEffect(() => {
    const quill = quillRef.current;
    if (
      !quill ||
      htmlContent === undefined ||
      htmlContent === quill.root.innerHTML ||
      htmlContent === normalizeQuillLists(quill.root.innerHTML)
    ) return;

    const selection = quill.getSelection();
    // Paste also sets a selection and focuses the editor, even with source "silent".
    quill.setContents(quill.clipboard.convert({ html: htmlContent || "", text: "" }), "silent");
    acceptedLengthRef.current = ValidatorUtils.getTextLength(quill.root.innerHTML);
    if (selection) {
      quill.setSelection(Math.min(selection.index, quill.getLength() - 1), selection.length, "silent");
    }
  }, [htmlContent]);

  return (
    <>
      <div className={`quill ${short ? "short-text" : ""} ${isIncomplete ? "border border-2 border-danger" : ""}`}>
        <div ref={editorRef} />
      </div>
      {lengthLimitReached && (
        <div className="text-danger text-end" role="alert" aria-live="polite">
          {intl.formatMessage({ id: "app.validation.maxLengthReached" }, { length: maxLength })}
        </div>
      )}
    </>
  );
};

export default RichTextEditor;
